import Vue from 'vue';
import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import moment from 'moment';
import API from '@/lib/API';
import { SOCKET_LEAD_UPDATED } from '@/store/actions/leads';
import { SOCKET_OPERATION_UPDATED, SOCKET_ZIP_UPLOAD_PROGRESS, SOCKET_ZIP_VERIFY_PROGRESS } from '@/store/actions/operations';
import {
  SocketLeadUpdatedData, LeadType,
  OperationsPool,
  Operation,
  LeadStatus,
  LogicType,
  WarehouseType,
  ShipmentStatus,
  InstallationDateStatus,
  InstallationStatus,
  OperationsFilters,
  OperationsFilterPreset,
  OperationChildUser,
  BoxOutcomeType,
  BoxStatusType,
  LeadUpdatedData,
  ConnectionStatusType,
  User,
  ArchivedStatus,
  ZipProgressData,
  PracticeAssigneesStatus,
  ReadyForFinalPaymentFilter,
  ShipmentCompletedFilter,
  InternetConnectionFilter,
  ServiceConfirmFilter,
  InverterConnectedFilter,
  TestingDateConfirmFilter,
  DocsSentToPartnerFilter,
} from '@/types';

moment.locale('it');

// Numero di commesse per pagina
const limit = 30;

// Ritorna l'elenco degli id delle figlie di una commessa
function getChildrenIds(operation: Operation): number[] {
  if (!operation || !operation.childrenIds || operation.childrenIds.length < 3) return [];

  return operation.childrenIds.substring(1, operation.childrenIds.length - 1).split(':')
    .map((s: string) => Number(s));
}

// In base ai filtri impostati, restituisce i parametri da utilizzare
// nelle API.
function getParamsFromFilters(filters) {
  console.log('store/operations - getParamsFromFilters filters =', filters); // eslint-disable-line no-console

  const params = {
  } as {
    offset?: number;
    limit?: number;
    operations?: boolean;
    search?: string;
    requester: number | null;
    owner: number[] | null;
    ownerLogic: LogicType;
    childType?: LeadType[];
    projectStatus?: BoxStatusType[];
    warehouse?: (WarehouseType | 'null')[];
    shipmentStatus?: ShipmentStatus[];

    partialInstallationDateStatus?: InstallationDateStatus | null;
    partialInstallationStatus?: InstallationStatus | null;
    installationDateStatus?: InstallationDateStatus | null;
    installationStatus?: InstallationStatus | null;

    dicoStatus?: BoxStatusType[];

    readyForFinalPayment?: ReadyForFinalPaymentFilter;
    shipmentCompleted?: ShipmentCompletedFilter;
    internetConnection?: InternetConnectionFilter;
    serviceConfirm?: ServiceConfirmFilter;
    inverterConnected?: InverterConnectedFilter;
    testingDateConfirm?: TestingDateConfirmFilter;
    docsSentToPartner?: DocsSentToPartnerFilter;

    testingOutcome?: (BoxOutcomeType | 'nodate')[];
    egridConnectionStatus?: ConnectionStatusType[];
    archivedStatus?: ArchivedStatus[];
    childStatus?: LeadStatus[];
    fatherStatus?: LeadStatus[];
    taskO?: string[];
    taskCm?: string[];
    opType: string[];
    payments: number[];
    missingFinishings?: BoxStatusType[];
    sortBy: string[];
    sortDesc: boolean[];
    dates?: {
      value: string;
      begin: string | false;
      end: string | false;
    };
    searchItems?: string[];
    searchValue?: string | null;
    searchLogic?: 'AND' | 'OR';
    searchExclude?: string | null;
    practiceAssignees?: (string | 'null')[];
    practiceAssigneesLogic?: LogicType;
    practiceAssigneesStatus?: PracticeAssigneesStatus | 'null';
    practiceAssigneesNegated?: (number | string)[];
    projectAssignees?: (string | 'null')[];
    projectAssigneesLogic?: LogicType;
    projectAssigneesStatus?: PracticeAssigneesStatus | 'null';
    projectAssigneesNegated?: (number | string)[];
    eneaAssignees?: (string | 'null')[];
    eneaAssigneesLogic?: LogicType;
    eneaAssigneesStatus?: PracticeAssigneesStatus | 'null';
    eneaAssigneesNegated?: (number | string)[];
    doganeAssignees?: (string | 'null')[];
    doganeAssigneesLogic?: LogicType;
    doganeAssigneesStatus?: PracticeAssigneesStatus | 'null';
    doganeAssigneesNegated?: (number | string)[];
    installerAssignees?: (string | 'null')[];
    installerAssigneesLogic?: LogicType;
    installerAssigneesStatus?: PracticeAssigneesStatus | 'null';
    installerAssigneesNegated?: (number | string)[];
    categories: number[];
    categoriesLogic: LogicType;
    categoriesNegated?: (number | string)[];
    tags: number[];
    tagsLogic: LogicType;
    tagsNegated?: (number | string)[];
    provinces: string[];
    provincesLogic: LogicType;
    provincesNegated?: (number | string)[];
    ownerNegated?: (number | string)[];
  };

  if (filters && 'search' in filters
    && typeof filters.search === 'string' && filters.search.length > 0) {
    params.search = filters.search;
  }

  if (filters && 'childType' in filters
    && Array.isArray(filters.childType) && filters.childType.length > 0) {
    params.childType = filters.childType;
  }

  if (filters && 'projectStatus' in filters
    && Array.isArray(filters.projectStatus) && filters.projectStatus.length > 0) {
    params.projectStatus = filters.projectStatus;
  }

  if (filters && 'warehouse' in filters
    && Array.isArray(filters.warehouse) && filters.warehouse.length > 0) {
    params.warehouse = filters.warehouse;
  }

  if (filters && 'shipmentStatus' in filters
    && Array.isArray(filters.shipmentStatus) && filters.shipmentStatus.length > 0) {
    params.shipmentStatus = filters.shipmentStatus;
  }

  // partialInstallationDateStatus, partialInstallationStatus
  if (filters && 'partialInstallationDateStatus' in filters
    && filters.partialInstallationDateStatus !== null) {
    params.partialInstallationDateStatus = filters.partialInstallationDateStatus;
  }
  if (filters && 'partialInstallationStatus' in filters
    && filters.partialInstallationStatus !== null) {
    params.partialInstallationStatus = filters.partialInstallationStatus;
  }

  // installationDateStatus, installationStatus
  if (filters && 'installationDateStatus' in filters
    && filters.installationDateStatus !== null) {
    params.installationDateStatus = filters.installationDateStatus;
  }
  if (filters && 'installationStatus' in filters
    && filters.installationStatus !== null) {
    params.installationStatus = filters.installationStatus;
  }

  if (filters && 'dicoStatus' in filters
    && Array.isArray(filters.dicoStatus) && filters.dicoStatus.length > 0) {
    params.dicoStatus = filters.dicoStatus;
  }

  if (filters && 'readyForFinalPayment' in filters
    && (filters.readyForFinalPayment === 'ready' || filters.readyForFinalPayment === 'not-ready')) {
    params.readyForFinalPayment = filters.readyForFinalPayment;
  }

  if (filters && 'shipmentCompleted' in filters
    && (filters.shipmentCompleted === 'completed' || filters.shipmentCompleted === 'not-completed')) {
    params.shipmentCompleted = filters.shipmentCompleted;
  }

  if (filters && 'internetConnection' in filters
    && (filters.internetConnection === 'tested' || filters.internetConnection === 'not-tested')) {
    params.internetConnection = filters.internetConnection;
  }

  if (filters && 'serviceConfirm' in filters
    && (filters.serviceConfirm === 'confirmed' || filters.serviceConfirm === 'not-confirmed')) {
    params.serviceConfirm = filters.serviceConfirm;
  }

  if (filters && 'inverterConnected' in filters
    && (filters.inverterConnected === 'connected' || filters.inverterConnected === 'not-connected')) {
    params.inverterConnected = filters.inverterConnected;
  }

  if (filters && 'testingDateConfirm' in filters
    && (filters.testingDateConfirm === 'confirmed' || filters.testingDateConfirm === 'not-confirmed')) {
    params.testingDateConfirm = filters.testingDateConfirm;
  }

  if (filters && 'docsSentToPartner' in filters
    && (filters.docsSentToPartner === 'sent' || filters.docsSentToPartner === 'not-sent')) {
    params.docsSentToPartner = filters.docsSentToPartner;
  }

  if (filters && 'testingOutcome' in filters
    && Array.isArray(filters.testingOutcome)
    && filters.testingOutcome.length > 0) {
    params.testingOutcome = filters.testingOutcome;
  }

  if (filters && 'egridConnectionStatus' in filters
    && Array.isArray(filters.egridConnectionStatus)
    && filters.egridConnectionStatus.length > 0) {
    params.egridConnectionStatus = filters.egridConnectionStatus;
  }

  if (filters && 'archivedStatus' in filters
    && Array.isArray(filters.archivedStatus)
    && filters.archivedStatus.length > 0) {
    params.archivedStatus = filters.archivedStatus;
  }

  if (filters && 'childStatus' in filters
  && Array.isArray(filters.childStatus) && filters.childStatus.length > 0) {
    params.childStatus = filters.childStatus;
  }

  if (filters && 'fatherStatus' in filters
  && Array.isArray(filters.fatherStatus) && filters.fatherStatus.length > 0) {
    params.fatherStatus = filters.fatherStatus;
  }

  if (filters && 'taskO' in filters
    && Array.isArray(filters.taskO) && filters.taskO.length > 0) {
    params.taskO = filters.taskO;
  }

  if (filters && 'taskCm' in filters
    && Array.isArray(filters.taskCm) && filters.taskCm.length > 0) {
    params.taskCm = filters.taskCm;
  }

  if (filters && 'requester' in filters) {
    params.requester = filters.requester;
  }

  if (filters && 'owner' in filters
    && Array.isArray(filters.owner) && filters.owner.length > 0) {
    params.owner = filters.owner;
    params.ownerLogic = filters.ownerLogic || 'OR';
  }

  if (filters && 'opType' in filters
    && Array.isArray(filters.opType) && filters.opType.length > 0) {
    params.opType = filters.opType;
  }

  if (filters && 'payments' in filters
    && Array.isArray(filters.payments) && filters.payments.length > 0) {
    params.payments = filters.payments;
  }

  if (filters && 'missingFinishings' in filters
    && Array.isArray(filters.missingFinishings) && filters.missingFinishings.length > 0) {
    params.missingFinishings = filters.missingFinishings;
  }

  if (filters && 'sortBy' in filters
    && Array.isArray(filters.sortBy) && filters.sortBy.length > 0) {
    params.sortBy = filters.sortBy;
  }

  if (filters && 'sortDesc' in filters
    && Array.isArray(filters.sortDesc) && filters.sortDesc.length > 0) {
    params.sortDesc = filters.sortDesc;
  }

  if (filters && 'dates' in filters
    && filters.dates.value
    && filters.dates.begin !== null
    && filters.dates.end !== null) {
    params.dates = {
      value: filters.dates.value,
      begin: filters.dates.begin !== false ? moment(filters.dates.begin).format('YYYY-MM-DD') : false,
      end: filters.dates.end !== false ? moment(filters.dates.end).format('YYYY-MM-DD') : false,
    };
  }

  const hasSearchItems = filters && 'searchItems' in filters
    && Array.isArray(filters.searchItems)
    && filters.searchItems.length > 0;
  const hasSearchValue = !!filters?.searchValue;
  const hasSearchExclude = filters?.searchExclude !== undefined && filters?.searchExclude !== null;
  if (hasSearchItems && (hasSearchValue || hasSearchExclude)) {
    params.searchItems = filters!.searchItems;
    if (hasSearchValue) {
      params.searchValue = filters!.searchValue;
      if (filters!.searchLogic === 'AND' || filters!.searchLogic === 'OR') {
        params.searchLogic = filters!.searchLogic;
      }
    }
    if (hasSearchExclude) {
      params.searchExclude = filters!.searchExclude;
    }
  }

  // Practice assignees
  if (filters.practiceAssignees !== undefined) {
    if (!filters.practiceAssignees) {
      params.practiceAssignees = [];
    } else {
      params.practiceAssignees = filters.practiceAssignees.map((value) => (value === null ? 'null' : String(value)));
    }
  }

  if (filters.practiceAssigneesLogic !== undefined) {
    params.practiceAssigneesLogic = filters.practiceAssigneesLogic ?? 'OR';
  }

  if (filters?.practiceAssigneesStatus) {
    params.practiceAssigneesStatus = filters.practiceAssigneesStatus;
  }

  if ((filters?.practiceAssigneesLogic === 'AND' || filters?.practiceAssigneesLogic === 'OR')
    && filters?.practiceAssigneesNegated !== undefined && Array.isArray(filters.practiceAssigneesNegated)) {
    params.practiceAssigneesNegated = filters.practiceAssigneesNegated;
  }

  // Project assignees
  if (filters.projectAssignees !== undefined) {
    if (!filters.projectAssignees) {
      params.projectAssignees = [];
    } else {
      params.projectAssignees = filters.projectAssignees.map((value) => (value === null ? 'null' : String(value)));
    }
  }

  if (filters.projectAssigneesLogic !== undefined) {
    params.projectAssigneesLogic = filters.projectAssigneesLogic ?? 'OR';
  }

  if (filters?.projectAssigneesStatus) {
    params.projectAssigneesStatus = filters.projectAssigneesStatus;
  }

  if ((filters?.projectAssigneesLogic === 'AND' || filters?.projectAssigneesLogic === 'OR')
    && filters?.projectAssigneesNegated !== undefined && Array.isArray(filters.projectAssigneesNegated)) {
    params.projectAssigneesNegated = filters.projectAssigneesNegated;
  }

  // ENEA assignees
  if (filters.eneaAssignees !== undefined) {
    if (!filters.eneaAssignees) {
      params.eneaAssignees = [];
    } else {
      params.eneaAssignees = filters.eneaAssignees.map((value) => (value === null ? 'null' : String(value)));
    }
  }

  if (filters.eneaAssigneesLogic !== undefined) {
    params.eneaAssigneesLogic = filters.eneaAssigneesLogic ?? 'OR';
  }

  if (filters?.eneaAssigneesStatus) {
    params.eneaAssigneesStatus = filters.eneaAssigneesStatus;
  }

  if ((filters?.eneaAssigneesLogic === 'AND' || filters?.eneaAssigneesLogic === 'OR')
    && filters?.eneaAssigneesNegated !== undefined && Array.isArray(filters.eneaAssigneesNegated)) {
    params.eneaAssigneesNegated = filters.eneaAssigneesNegated;
  }

  // Dogane assignees
  if (filters.doganeAssignees !== undefined) {
    if (!filters.doganeAssignees) {
      params.doganeAssignees = [];
    } else {
      params.doganeAssignees = filters.doganeAssignees.map((value) => (value === null ? 'null' : String(value)));
    }
  }

  if (filters.doganeAssigneesLogic !== undefined) {
    params.doganeAssigneesLogic = filters.doganeAssigneesLogic ?? 'OR';
  }

  if (filters?.doganeAssigneesStatus) {
    params.doganeAssigneesStatus = filters.doganeAssigneesStatus;
  }

  if ((filters?.doganeAssigneesLogic === 'AND' || filters?.doganeAssigneesLogic === 'OR')
    && filters?.doganeAssigneesNegated !== undefined && Array.isArray(filters.doganeAssigneesNegated)) {
    params.doganeAssigneesNegated = filters.doganeAssigneesNegated;
  }

  // Installer assignees
  if (filters.installerAssignees !== undefined) {
    if (!filters.installerAssignees) {
      params.installerAssignees = [];
    } else {
      params.installerAssignees = filters.installerAssignees.map((value) => (value === null ? 'null' : String(value)));
    }
  }

  if (filters.installerAssigneesLogic !== undefined) {
    params.installerAssigneesLogic = filters.installerAssigneesLogic ?? 'OR';
  }

  if (filters?.installerAssigneesStatus) {
    params.installerAssigneesStatus = filters.installerAssigneesStatus;
  }

  if ((filters?.installerAssigneesLogic === 'AND' || filters?.installerAssigneesLogic === 'OR')
    && filters?.installerAssigneesNegated !== undefined && Array.isArray(filters.installerAssigneesNegated)) {
    params.installerAssigneesNegated = filters.installerAssigneesNegated;
  }

  if (filters && 'categories' in filters
    && Array.isArray(filters.categories) && filters.categories.length > 0) {
    params.categories = filters.categories;
  }

  if (filters && 'categoriesLogic' in filters
    && (
      filters.categoriesLogic === 'AND'
      || filters.categoriesLogic === 'OR'
      || filters.categoriesLogic === 'NOT'
    )
  ) {
    params.categoriesLogic = filters.categoriesLogic;
  }
  if ((filters?.categoriesLogic === 'AND' || filters?.categoriesLogic === 'OR')
    && filters && 'categoriesNegated' in filters && Array.isArray(filters.categoriesNegated)) {
    params.categoriesNegated = filters.categoriesNegated;
  }

  if (filters && 'tags' in filters
    && Array.isArray(filters.tags) && filters.tags.length > 0) {
    params.tags = filters.tags;
  }

  if (filters && 'tagsLogic' in filters
    && (
      filters.tagsLogic === 'AND'
      || filters.tagsLogic === 'OR'
      || filters.tagsLogic === 'NOT'
    )
  ) {
    params.tagsLogic = filters.tagsLogic;
  }
  if ((filters?.tagsLogic === 'AND' || filters?.tagsLogic === 'OR')
    && filters && 'tagsNegated' in filters && Array.isArray(filters.tagsNegated)) {
    params.tagsNegated = filters.tagsNegated;
  }

  if (filters && 'provinces' in filters
    && Array.isArray(filters.provinces) && filters.provinces.length > 0) {
    params.provinces = filters.provinces;
  }

  if (filters && 'provincesLogic' in filters
    && (
      filters.provincesLogic === 'AND'
      || filters.provincesLogic === 'OR'
      || filters.provincesLogic === 'NOT'
    )
  ) {
    params.provincesLogic = filters.provincesLogic;
  }
  if ((filters?.provincesLogic === 'AND' || filters?.provincesLogic === 'OR')
    && filters && 'provincesNegated' in filters && Array.isArray(filters.provincesNegated)) {
    params.provincesNegated = filters.provincesNegated;
  }

  if ((filters?.ownerLogic === 'AND' || filters?.ownerLogic === 'OR')
    && filters && 'ownerNegated' in filters && Array.isArray(filters.ownerNegated)) {
    params.ownerNegated = filters.ownerNegated;
  }

  console.log('store/operations - getParamsFromFilters params =', params); // eslint-disable-line no-console

  return params;
}

@Module
export default class Operations extends VuexModule {
  // State

  // Pool dei progetti mostrati (incluse i progetti figli richiesti)
  pool: OperationsPool = {}
  poolUpdating: {
    [key: number]: boolean;
  } = {}
  poolDirty: {
    [key: number]: boolean;
  } = {}

  // Commessa selezionata
  operation: number | null = null
  operationUpdating = false

  // Ritorna il progetto selezionato
  get currentOperation(): Operation | null {
    return this.operation !== null && this.operation in this.pool
      ? this.pool[this.operation] : null;
  }

  // Elenco degli id dei progetti mostrati
  list: number[] = []

  filters: {
    search: string | null;
    requester: number | null;
    owner: number[] | null;
    ownerLogic: LogicType;

    childType: LeadType[];
    projectStatus: BoxStatusType[];
    warehouse: (WarehouseType | 'null')[];
    shipmentStatus: ShipmentStatus[];

    partialInstallationDateStatus: InstallationDateStatus | null;
    partialInstallationStatus: InstallationStatus | null;
    installationDateStatus: InstallationDateStatus | null;
    installationStatus: InstallationStatus | null;

    dicoStatus: BoxStatusType[];

    readyForFinalPayment: ReadyForFinalPaymentFilter | null;
    shipmentCompleted: ShipmentCompletedFilter | null;
    internetConnection: InternetConnectionFilter | null;
    serviceConfirm: ServiceConfirmFilter | null;
    inverterConnected: InverterConnectedFilter | null;
    testingDateConfirm: TestingDateConfirmFilter | null;
    docsSentToPartner: DocsSentToPartnerFilter | null;

    testingOutcome: (BoxOutcomeType | 'nodate')[];
    egridConnectionStatus: ConnectionStatusType[];
    archivedStatus: ArchivedStatus[];
    childStatus: LeadStatus[];
    fatherStatus: LeadStatus[];
    taskO: string[];
    taskCm: string[];
    opType: string[];
    payments: number[];
    missingFinishings: BoxStatusType[];

    sortBy: string[];
    sortDesc: boolean[];

    dates: {
      value: string | null;
      begin: Date | null | false;
      end: Date | null | false;
    };

    searchItems: string[];
    searchValue: string | null;
    searchLogic?: 'AND' | 'OR';
    searchExclude?: string | null;
    categories: number[];
    categoriesLogic: LogicType;
    categoriesNegated: (number | string)[];
    tags: number[];
    tagsLogic: LogicType;
    tagsNegated: (number | string)[];
    provinces: string[];
    provincesLogic: LogicType;
    provincesNegated: (number | string)[];
    ownerNegated: (number | string)[];

    practiceAssignees: (number | null)[];
    practiceAssigneesLogic: LogicType;
    practiceAssigneesStatus: PracticeAssigneesStatus | null;
    practiceAssigneesNegated: (number | string)[];
    projectAssignees: (number | null)[];
    projectAssigneesLogic: LogicType;
    projectAssigneesStatus: PracticeAssigneesStatus | null;
    projectAssigneesNegated: (number | string)[];
    eneaAssignees: (number | null)[];
    eneaAssigneesLogic: LogicType;
    eneaAssigneesStatus: PracticeAssigneesStatus | null;
    eneaAssigneesNegated: (number | string)[];
    doganeAssignees: (number | null)[];
    doganeAssigneesLogic: LogicType;
    doganeAssigneesStatus: PracticeAssigneesStatus | null;
    doganeAssigneesNegated: (number | string)[];
    installerAssignees: (number | null)[];
    installerAssigneesLogic: LogicType;
    installerAssigneesStatus: PracticeAssigneesStatus | null;
    installerAssigneesNegated: (number | string)[];
  } = {
    search: null,
    requester: null,
    owner: null,
    ownerLogic: 'OR' as LogicType,
    childType: [],
    projectStatus: [],
    warehouse: [],
    shipmentStatus: [],

    partialInstallationDateStatus: null,
    partialInstallationStatus: null,
    installationDateStatus: null,
    installationStatus: null,

    dicoStatus: [],

    readyForFinalPayment: null,
    shipmentCompleted: null,
    internetConnection: null,
    serviceConfirm: null,
    inverterConnected: null,
    testingDateConfirm: null,
    docsSentToPartner: null,

    testingOutcome: [],
    egridConnectionStatus: [],
    archivedStatus: [],
    childStatus: [],
    fatherStatus: [],
    taskO: [],
    taskCm: [],
    opType: [],
    payments: [],
    missingFinishings: [],
    sortBy: [],
    sortDesc: [],
    dates: {
      value: null,
      begin: null,
      end: null,
    },
    searchItems: [],
    searchValue: null,
    searchLogic: 'OR' as 'AND' | 'OR',
    searchExclude: null,
    categories: [],
    categoriesLogic: 'AND',
    categoriesNegated: [],
    tags: [],
    tagsLogic: 'AND',
    tagsNegated: [],
    provinces: [],
    provincesLogic: 'AND',
    provincesNegated: [],
    ownerNegated: [],

    practiceAssignees: [],
    practiceAssigneesLogic: 'OR',
    practiceAssigneesStatus: null,
    practiceAssigneesNegated: [],
    projectAssignees: [],
    projectAssigneesLogic: 'OR',
    projectAssigneesStatus: null,
    projectAssigneesNegated: [],
    eneaAssignees: [],
    eneaAssigneesLogic: 'OR',
    eneaAssigneesStatus: null,
    eneaAssigneesNegated: [],
    doganeAssignees: [],
    doganeAssigneesLogic: 'OR',
    doganeAssigneesStatus: null,
    doganeAssigneesNegated: [],
    installerAssignees: [],
    installerAssigneesLogic: 'OR',
    installerAssigneesStatus: null,
    installerAssigneesNegated: [],
  }

  // Singolo progetto selezionato
  // project: number | null = null

  operationsUpdating = false

  operationsUpdateRequired = false

  // Preset dei filtri Operations
  operationsFilterPresets: OperationsFilterPreset[] = []

  get currentOperationsFilterPresets(): OperationsFilterPreset[] {
    return this.operationsFilterPresets;
  }

  // childrenToGet: number[] = []
  // childrenInterval: number | undefined

  operationsCount = 0

  // Ritorna il pool con tutti i progetti mostrati e utilizzati (include i progetti figli)
  get currentOperationsPool(): OperationsPool {
    return this.pool;
  }

  get currentOperationsPoolUpdating(): { [key: number]: boolean } {
    return this.poolUpdating;
  }

  // Ritorna l'elenco di lead attuale
  get operationsList(): Operation[] {
    const result: Operation[] = [];

    this.list.forEach((id) => {
      if (id in this.pool) result.push(this.pool[id]);
    });

    return result;
  }

  get areOperationsUpdating(): boolean { return this.operationsUpdating; }

  get areOperationsUpdateRequired(): boolean { return this.operationsUpdateRequired; }

  get currentOperationsFilters() { return this.filters; }

  get currentOperationsCount(): number { return this.operationsCount; }

  zipUploadProgress: ZipProgressData | null = null
  get currentZipUploadProgress(): ZipProgressData | null { return this.zipUploadProgress; }

  zipVerifyProgress: ZipProgressData | null = null
  get currentZipVerifyProgress(): ZipProgressData | null { return this.zipVerifyProgress; }

  // Mutations

  @Mutation // rev 1
  setZipUploadProgress({ progress }: { progress: ZipProgressData }) {
    this.zipUploadProgress = progress;
  }

  @Mutation // rev 1
  setZipVerifyProgress({ progress }: { progress: ZipProgressData }) {
    this.zipVerifyProgress = progress;
  }

  @Mutation // rev 1
  setOperationUpdating() { this.operationUpdating = true; }

  @Mutation // rev 1
  unsetOperationUpdating() { this.operationUpdating = false; }

  @Mutation // rev 1
  setOperation({ operation }: { operation: Operation }) {
    // Seleziona il progetto attuale
    this.operation = operation.id;
  }

  @Mutation
  addOperationsToList({ operations }: { operations: Operation[] }) { // rev 1
    console.log('store/operations - mutations:addOperationsToList '); // eslint-disable-line no-console

    // Crea un elenco con gli id delle commesse
    this.list = this.list.concat(operations.map((o) => o.id));

    // Inserisce le commesse nel pool
    operations.forEach((o) => {
      // Separa i dati delle commesse (oData) dai dati delle figlie (childrenData, childrenIds)
      const { childrenData, childrenIds, ...oData } = o;
      const children = JSON.parse(childrenData);

      if (
        (this.filters.childType !== undefined && this.filters.childType.length > 0)
        || (this.filters.childStatus !== undefined && this.filters.childStatus.length > 0)
        || this.filters.requester !== null
        || (this.filters.owner !== null && Array.isArray(this.filters.owner) && this.filters.owner.length > 0)
      ) {
        // C'è almeno un filtro impostato tra childType, childStatus, requester e owner

        // Vanno filtrate ulteriormente le lead figlie
        (oData as Operation).children = {};

        // eslint-disable-next-line no-restricted-syntax
        for (const childId in children) {
          if (Object.prototype.hasOwnProperty.call(children, childId)) {
            const child = children[childId];

            let selectedByType = true;
            let selectedByStatus = child.status !== 'cancelled';
            let selectedByRequester = this.filters.requester === null;
            let selectedByOwner = this.filters.owner === null || (Array.isArray(this.filters.owner) && this.filters.owner.length === 0);

            if (this.filters.childType !== undefined && this.filters.childType.length > 0) {
              selectedByType = this.filters.childType.includes(child.type);
            }
            if (this.filters.childStatus !== undefined && this.filters.childStatus.length > 0) {
              selectedByStatus = this.filters.childStatus.includes(child.status);
            }
            if (this.filters.requester !== null) {
              selectedByRequester = child.requester.id === this.filters.requester;
            }
            if (this.filters.owner !== null && Array.isArray(this.filters.owner) && this.filters.owner.length > 0) {
              if (this.filters.ownerLogic === 'OR') {
                // Almeno uno degli owner deve essere presente
                selectedByOwner = Array.isArray(child.owners)
                  && this.filters.owner.some((ownerId) => (child.owners as OperationChildUser[])
                    .findIndex((i) => i.id === ownerId) > -1);
              } else if (this.filters.ownerLogic === 'NOT') {
                // Nessuno degli owner deve essere presente
                selectedByOwner = Array.isArray(child.owners)
                  && !this.filters.owner.some((ownerId) => (child.owners as OperationChildUser[])
                    .findIndex((i) => i.id === ownerId) > -1);
              } else { // AND
                // Tutti gli owner devono essere presenti
                selectedByOwner = Array.isArray(child.owners)
                  && this.filters.owner.every((ownerId) => (child.owners as OperationChildUser[])
                    .findIndex((i) => i.id === ownerId) > -1);
              }
            }

            if (selectedByType && selectedByStatus && selectedByRequester && selectedByOwner) {
              (oData as Operation).children[childId] = child;
            }
          }
        }

        const filtredChildrenIds = getChildrenIds(o).filter((cId) => cId in oData.children);
        (oData as Operation).childrenIds = `:${filtredChildrenIds.join(':')}:`;
      } else {
        // Non sono impostati i filtri childType, childStatus, requester o owner

        // Considera tutte le figlie
        (oData as Operation).children = children;
        (oData as Operation).childrenIds = childrenIds;
      }

      // Se è attivo un filtro sullo stato delle figlie (childStatus)...
      if (this.filters.childStatus !== undefined && this.filters.childStatus.length > 0) {
        // Aggiunge la commessa al pool soltanto se ha almeno una figlia che soddisfa il filtro
        if (Object.keys((oData as Operation).children).length > 0) {
          Vue.set(this.pool, o.id, oData);
          Vue.set(this.poolUpdating, o.id, false);
          Vue.set(this.poolDirty, o.id, false);
        }
      } else {
        // Non è attivo un filtro sullo stato delle figlie (childStatus)
        // Aggiunge sempre la commessa al pool
        Vue.set(this.pool, o.id, oData);
        Vue.set(this.poolUpdating, o.id, false);
        Vue.set(this.poolDirty, o.id, false);
      }
    });
  }

  @Mutation // rev 1
  setOperationsUpdating() { this.operationsUpdating = true; }

  @Mutation // rev 1
  unsetOperationsUpdating() { this.operationsUpdating = false; }

  @Mutation // rev 1
  setOperationsUpdateRequired() { this.operationsUpdateRequired = true; }

  @Mutation // rev 1
  unsetOperationsUpdateRequired() { this.operationsUpdateRequired = false; }

  @Mutation
  addOperationToPool(
    { operation, full = false }: { operation: Operation; full: boolean },
  ) { // rev 1
    // Aggiunge/aggiorna il progetto nel pool
    const { childrenData, childrenIds, ...oData } = operation;
    const children = JSON.parse(childrenData);

    if (full
      || (this.filters.childType.length === 0 && this.filters.childStatus.length === 0)) {
      // Non sono impostati i filtri childType e childStatus
      // o è stata richiesta la versione completa

      // Considera tutti i figli
      (oData as Operation).children = children;
      (oData as Operation).childrenIds = childrenIds;
    } else {
      // C'è almeno un filtro impostato tra childType e childStatus
      // Sceglie quali figlie mostrare

      (oData as Operation).children = {};

      // eslint-disable-next-line no-restricted-syntax
      for (const childId in children) {
        if (Object.prototype.hasOwnProperty.call(children, childId)) {
          const child = children[childId];

          let selectedByType = this.filters.childType.length === 0;
          let selectedByStatus = this.filters.childStatus.length === 0;

          if (this.filters.childType.length > 0) {
            selectedByType = this.filters.childType.includes(child.type);
          }
          if (this.filters.childStatus.length > 0) {
            selectedByStatus = this.filters.childStatus.includes(child.status);
          }

          if (selectedByType && selectedByStatus) {
            (oData as Operation).children[childId] = child;
          }
        }
      }

      const filtredChildrenIds = getChildrenIds(operation).filter((cId) => cId in oData.children);
      (oData as Operation).childrenIds = `:${filtredChildrenIds.join(':')}:`;
    }

    Vue.set(this.pool, operation.id, oData);
    Vue.set(this.poolUpdating, operation.id, false);
    Vue.set(this.poolDirty, operation.id, false);
  }

  @Mutation
  updateOperationChildFields(
    { operationId, childId, fields }: { operationId: number; childId: number; fields: Record<string, unknown> },
  ) {
    const operation = this.pool[operationId];
    if (!operation?.children?.[childId]) return;
    const child = operation.children[childId];
    Object.entries(fields).forEach(([key, value]) => {
      Vue.set(child, key, value);
    });
  }

  @Mutation
  removeOperationFromPool({ id }: { id: number }) { // rev 1
    console.log('mutations:removeOperationFromPool'); // eslint-disable-line no-console
    // Rimuove il progetto dalnel pool
    Vue.delete(this.pool, id);
    Vue.delete(this.poolUpdating, id);
    Vue.delete(this.poolDirty, id);
  }

  @Mutation
  setOperationsFilters({ filters, save = true, permanent = true }: { filters: OperationsFilters; save: boolean; permanent: boolean }) {
    console.log('store/operations - mutations:setOperationsFilters =', filters); // eslint-disable-line no-console

    if (filters?.search !== undefined) Vue.set(this.filters, 'search', filters.search);
    else Vue.delete(this.filters, 'search');

    if (filters?.requester !== undefined) Vue.set(this.filters, 'requester', filters.requester);
    else Vue.delete(this.filters, 'requester');

    if (filters?.owner !== undefined) Vue.set(this.filters, 'owner', filters.owner);
    else Vue.delete(this.filters, 'owner');

    if (filters?.ownerLogic !== undefined) Vue.set(this.filters, 'ownerLogic', filters.ownerLogic);
    else Vue.delete(this.filters, 'ownerLogic');

    if (filters?.childType !== undefined) Vue.set(this.filters, 'childType', filters.childType);
    else Vue.delete(this.filters, 'childType');

    if (filters?.projectStatus !== undefined) Vue.set(this.filters, 'projectStatus', filters.projectStatus);
    else Vue.delete(this.filters, 'projectStatus');

    if (filters?.warehouse !== undefined) Vue.set(this.filters, 'warehouse', filters.warehouse);
    else Vue.delete(this.filters, 'warehouse');

    if (filters?.shipmentStatus !== undefined) Vue.set(this.filters, 'shipmentStatus', filters.shipmentStatus);
    else Vue.delete(this.filters, 'shipmentStatus');

    if (filters?.partialInstallationDateStatus !== undefined) Vue.set(this.filters, 'partialInstallationDateStatus', filters.partialInstallationDateStatus);
    else Vue.delete(this.filters, 'partialInstallationDateStatus');
    if (filters?.partialInstallationStatus !== undefined) Vue.set(this.filters, 'partialInstallationStatus', filters.partialInstallationStatus);
    else Vue.delete(this.filters, 'partialInstallationStatus');

    if (filters?.installationDateStatus !== undefined) Vue.set(this.filters, 'installationDateStatus', filters.installationDateStatus);
    else Vue.delete(this.filters, 'installationDateStatus');
    if (filters?.installationStatus !== undefined) Vue.set(this.filters, 'installationStatus', filters.installationStatus);
    else Vue.delete(this.filters, 'installationStatus');

    if (filters?.dicoStatus !== undefined) Vue.set(this.filters, 'dicoStatus', filters.dicoStatus);
    else Vue.delete(this.filters, 'dicoStatus');

    if (filters?.readyForFinalPayment !== undefined) Vue.set(this.filters, 'readyForFinalPayment', filters.readyForFinalPayment);
    else Vue.delete(this.filters, 'readyForFinalPayment');

    if (filters?.shipmentCompleted !== undefined) Vue.set(this.filters, 'shipmentCompleted', filters.shipmentCompleted);
    else Vue.delete(this.filters, 'shipmentCompleted');

    if (filters?.internetConnection !== undefined) Vue.set(this.filters, 'internetConnection', filters.internetConnection);
    else Vue.delete(this.filters, 'internetConnection');

    if (filters?.serviceConfirm !== undefined) Vue.set(this.filters, 'serviceConfirm', filters.serviceConfirm);
    else Vue.delete(this.filters, 'serviceConfirm');

    if (filters?.inverterConnected !== undefined) Vue.set(this.filters, 'inverterConnected', filters.inverterConnected);
    else Vue.delete(this.filters, 'inverterConnected');

    if (filters?.testingDateConfirm !== undefined) Vue.set(this.filters, 'testingDateConfirm', filters.testingDateConfirm);
    else Vue.delete(this.filters, 'testingDateConfirm');

    if (filters?.docsSentToPartner !== undefined) Vue.set(this.filters, 'docsSentToPartner', filters.docsSentToPartner);
    else Vue.delete(this.filters, 'docsSentToPartner');

    if (filters?.testingOutcome !== undefined) Vue.set(this.filters, 'testingOutcome', filters.testingOutcome);
    else Vue.delete(this.filters, 'testingOutcome');

    if (filters?.egridConnectionStatus !== undefined) Vue.set(this.filters, 'egridConnectionStatus', filters.egridConnectionStatus);
    else Vue.delete(this.filters, 'egridConnectionStatus');

    if (filters?.archivedStatus !== undefined) Vue.set(this.filters, 'archivedStatus', filters.archivedStatus);
    else Vue.delete(this.filters, 'archivedStatus');

    if (filters?.childStatus !== undefined) Vue.set(this.filters, 'childStatus', filters.childStatus);
    else Vue.delete(this.filters, 'childStatus');

    if (filters?.fatherStatus !== undefined) Vue.set(this.filters, 'fatherStatus', filters.fatherStatus);
    else Vue.delete(this.filters, 'fatherStatus');

    if (filters?.taskO !== undefined) Vue.set(this.filters, 'taskO', filters.taskO);
    else Vue.delete(this.filters, 'taskO');

    if (filters?.taskCm !== undefined) Vue.set(this.filters, 'taskCm', filters.taskCm);
    else Vue.delete(this.filters, 'taskCm');

    if (filters?.opType !== undefined) Vue.set(this.filters, 'opType', filters.opType);
    else Vue.delete(this.filters, 'opType');

    if (filters?.payments !== undefined) Vue.set(this.filters, 'payments', filters.payments);
    else Vue.delete(this.filters, 'payments');

    if (filters?.missingFinishings !== undefined) {
      // Migrazione legacy boolean → array (copre preset salvati storicamente
      // e client vecchi).
      const v = filters.missingFinishings as unknown;
      let migrated: BoxStatusType[] = [];
      if (v === true) {
        migrated = ['todo'];
      } else if (Array.isArray(v)) {
        migrated = (v as BoxStatusType[]).filter((x) => x === 'todo' || x === 'done');
      }
      Vue.set(this.filters, 'missingFinishings', migrated);
    } else Vue.delete(this.filters, 'missingFinishings');

    if (filters?.sortBy !== undefined) Vue.set(this.filters, 'sortBy', filters.sortBy);
    else Vue.delete(this.filters, 'sortBy');

    if (filters?.sortDesc !== undefined) Vue.set(this.filters, 'sortDesc', filters.sortDesc);
    else Vue.delete(this.filters, 'sortDesc');

    if (filters?.dates !== undefined) Vue.set(this.filters, 'dates', filters.dates);
    else Vue.delete(this.filters, 'dates');

    if (filters?.categories !== undefined) Vue.set(this.filters, 'categories', filters.categories);
    else Vue.delete(this.filters, 'categories');

    if (filters?.categoriesLogic !== undefined) Vue.set(this.filters, 'categoriesLogic', filters.categoriesLogic);
    else Vue.delete(this.filters, 'categoriesLogic');

    if (filters?.categoriesNegated !== undefined) Vue.set(this.filters, 'categoriesNegated', filters.categoriesNegated);
    else Vue.delete(this.filters, 'categoriesNegated');

    if (filters?.tags !== undefined) Vue.set(this.filters, 'tags', filters.tags);
    else Vue.delete(this.filters, 'tags');

    if (filters?.tagsLogic !== undefined) Vue.set(this.filters, 'tagsLogic', filters.tagsLogic);
    else Vue.delete(this.filters, 'tagsLogic');

    if (filters?.tagsNegated !== undefined) Vue.set(this.filters, 'tagsNegated', filters.tagsNegated);
    else Vue.delete(this.filters, 'tagsNegated');

    if (filters?.provinces !== undefined) Vue.set(this.filters, 'provinces', filters.provinces);
    else Vue.delete(this.filters, 'provinces');

    if (filters?.provincesLogic !== undefined) Vue.set(this.filters, 'provincesLogic', filters.provincesLogic);
    else Vue.delete(this.filters, 'provincesLogic');

    if (filters?.provincesNegated !== undefined) Vue.set(this.filters, 'provincesNegated', filters.provincesNegated);
    else Vue.delete(this.filters, 'provincesNegated');

    if (filters?.ownerNegated !== undefined) Vue.set(this.filters, 'ownerNegated', filters.ownerNegated);
    else Vue.delete(this.filters, 'ownerNegated');

    if (filters?.practiceAssignees !== undefined) Vue.set(this.filters, 'practiceAssignees', filters.practiceAssignees);
    else Vue.delete(this.filters, 'practiceAssignees');

    if (filters?.practiceAssigneesLogic !== undefined) Vue.set(this.filters, 'practiceAssigneesLogic', filters.practiceAssigneesLogic);
    else Vue.delete(this.filters, 'practiceAssigneesLogic');

    if (filters?.practiceAssigneesStatus) Vue.set(this.filters, 'practiceAssigneesStatus', filters.practiceAssigneesStatus);
    else Vue.delete(this.filters, 'practiceAssigneesStatus');

    if (filters?.practiceAssigneesNegated !== undefined) Vue.set(this.filters, 'practiceAssigneesNegated', filters.practiceAssigneesNegated);
    else Vue.delete(this.filters, 'practiceAssigneesNegated');

    if (filters?.projectAssignees !== undefined) Vue.set(this.filters, 'projectAssignees', filters.projectAssignees);
    else Vue.delete(this.filters, 'projectAssignees');

    if (filters?.projectAssigneesLogic !== undefined) Vue.set(this.filters, 'projectAssigneesLogic', filters.projectAssigneesLogic);
    else Vue.delete(this.filters, 'projectAssigneesLogic');

    if (filters?.projectAssigneesStatus) Vue.set(this.filters, 'projectAssigneesStatus', filters.projectAssigneesStatus);
    else Vue.delete(this.filters, 'projectAssigneesStatus');

    if (filters?.projectAssigneesNegated !== undefined) Vue.set(this.filters, 'projectAssigneesNegated', filters.projectAssigneesNegated);
    else Vue.delete(this.filters, 'projectAssigneesNegated');

    if (filters?.eneaAssignees !== undefined) Vue.set(this.filters, 'eneaAssignees', filters.eneaAssignees);
    else Vue.delete(this.filters, 'eneaAssignees');

    if (filters?.eneaAssigneesLogic !== undefined) Vue.set(this.filters, 'eneaAssigneesLogic', filters.eneaAssigneesLogic);
    else Vue.delete(this.filters, 'eneaAssigneesLogic');

    if (filters?.eneaAssigneesStatus) Vue.set(this.filters, 'eneaAssigneesStatus', filters.eneaAssigneesStatus);
    else Vue.delete(this.filters, 'eneaAssigneesStatus');

    if (filters?.eneaAssigneesNegated !== undefined) Vue.set(this.filters, 'eneaAssigneesNegated', filters.eneaAssigneesNegated);
    else Vue.delete(this.filters, 'eneaAssigneesNegated');

    if (filters?.doganeAssignees !== undefined) Vue.set(this.filters, 'doganeAssignees', filters.doganeAssignees);
    else Vue.delete(this.filters, 'doganeAssignees');

    if (filters?.doganeAssigneesLogic !== undefined) Vue.set(this.filters, 'doganeAssigneesLogic', filters.doganeAssigneesLogic);
    else Vue.delete(this.filters, 'doganeAssigneesLogic');

    if (filters?.doganeAssigneesStatus) Vue.set(this.filters, 'doganeAssigneesStatus', filters.doganeAssigneesStatus);
    else Vue.delete(this.filters, 'doganeAssigneesStatus');

    if (filters?.doganeAssigneesNegated !== undefined) Vue.set(this.filters, 'doganeAssigneesNegated', filters.doganeAssigneesNegated);
    else Vue.delete(this.filters, 'doganeAssigneesNegated');

    if (filters?.installerAssignees !== undefined) Vue.set(this.filters, 'installerAssignees', filters.installerAssignees);
    else Vue.delete(this.filters, 'installerAssignees');

    if (filters?.installerAssigneesLogic !== undefined) Vue.set(this.filters, 'installerAssigneesLogic', filters.installerAssigneesLogic);
    else Vue.delete(this.filters, 'installerAssigneesLogic');

    if (filters?.installerAssigneesStatus) Vue.set(this.filters, 'installerAssigneesStatus', filters.installerAssigneesStatus);
    else Vue.delete(this.filters, 'installerAssigneesStatus');

    if (filters?.installerAssigneesNegated !== undefined) Vue.set(this.filters, 'installerAssigneesNegated', filters.installerAssigneesNegated);
    else Vue.delete(this.filters, 'installerAssigneesNegated');

    if (filters?.searchItems !== undefined) Vue.set(this.filters, 'searchItems', filters.searchItems);
    else Vue.delete(this.filters, 'searchItems');

    if (filters?.searchValue !== undefined) Vue.set(this.filters, 'searchValue', filters.searchValue);
    else Vue.delete(this.filters, 'searchValue');

    if (filters?.searchLogic !== undefined) Vue.set(this.filters, 'searchLogic', filters.searchLogic);
    else Vue.delete(this.filters, 'searchLogic');

    if (filters?.searchExclude !== undefined) Vue.set(this.filters, 'searchExclude', filters.searchExclude);
    else Vue.delete(this.filters, 'searchExclude');

    // Aggiorna i filtri associati all'utente memorizzati nel database
    if (save) {
      if (permanent) API.updateOperationsFilters(filters);
      else {
        // metto i filtri nella sessione di VueSession
        Vue.prototype.$session.set('operationsFilters', filters);
      }
    }
  }

  @Mutation
  setOperationsList(list: number[]) {
    console.log('store/operations - mutations:setOperationsList'); // eslint-disable-line no-console

    this.list = list;
  }

  @Mutation
  setPool(pool: OperationsPool) {
    console.log('store/operations - mutations:setPool'); // eslint-disable-line no-console

    this.pool = pool;
  }

  @Mutation
  setPoolUpdating(poolUpdating: {
    [key: number]: boolean;
  }) {
    console.log('store/operations - mutations:setPoolUpdating'); // eslint-disable-line no-console

    this.poolUpdating = poolUpdating;
  }

  @Mutation
  setPoolDirty(poolDirty: {
    [key: number]: boolean;
  }) {
    console.log('store/operations - mutations:setPoolDirty'); // eslint-disable-line no-console

    this.poolDirty = poolDirty;
  }

  @Mutation
  setOperationsCount(count: number) {
    console.log('store/operations - mutations:setOperationsCount'); // eslint-disable-line no-console

    this.operationsCount = count;
  }

  @Mutation
  setOperationsFilterPresets(presets: OperationsFilterPreset[]) {
    console.log('store/operations - mutations:setOperationsFilterPresets'); // eslint-disable-line no-console

    this.operationsFilterPresets = presets;
  }

  @Action({ rawError: true })
  clearOperationsList() {
    console.log('store/operations - actions:clearOperationsList'); // eslint-disable-line no-console

    this.context.commit('unsetOperationsUpdateRequired');

    this.context.commit('setOperationsList', []);

    this.context.commit('setPool', {});
    this.context.commit('setPoolUpdating', {});
    this.context.commit('setPoolDirty', {});
  }

  // Inizializza il valore dei filtri sulle commesse
  // recuperandoli dalle impostazione dell'utente memorizzate nel database o nella sessione
  @Action({ rawError: true })
  async initOperationsFilters() {
    console.log('store/operations - actions:initOperationsFilters'); // eslint-disable-line no-console

    // Forza il recupero dell'utente tramite API
    // perché i settings potrebbero essere cambiati.
    await this.context.dispatch('fetchUser');

    const currentUser = this.context.getters.currentUser as User | null;

    let operationsFilters: OperationsFilters | null = null;

    // Salva i filtri in sessione se l'utente ha scelto di non memorizzarli permanentemente
    if (currentUser?.permanentSettings === true) {
      const userSettings = currentUser.settings
        ? JSON.parse(currentUser.settings)
        : {};

      console.log('store/operations - actions:initOperationsFilters userSettings =', userSettings); // eslint-disable-line no-console

      operationsFilters = userSettings.operationsFilters ?? null;
    } else {
      operationsFilters = Vue.prototype.$session.get('operationsFilters') ?? null;
    }

    console.log('store/operations - actions:initOperationsFilters operationsFilters =', operationsFilters); // eslint-disable-line no-console

    // TODO: Utilizzare delle proprietà specifiche per i filtri su operations
    this.context.commit('setOperationsFilters', {
      filters: operationsFilters,
      save: false, // Evita di memorizzare nel database / sessione
      permanent: currentUser?.permanentSettings ?? false,
    });

    return Promise.resolve();
  }

  @Action({ rawError: true })
  async updateOperationsFilters(filters: OperationsFilters) {
    console.log('store/operations - actions:updateOperationsFilters'); // eslint-disable-line no-console

    const currentUser = this.context.getters.currentUser as User | null;

    this.context.commit('setOperationsFilters', {
      filters,
      permanent: currentUser?.permanentSettings ?? false,
    });
  }

  @Action({ rawError: true })
  async getOpNotes({ id }: { id: number }) {
    console.log('store/operations - actions:getOpNotes'); // eslint-disable-line no-console

    const result = await API.getOpNotes(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve(result);
  }

  // @Action({ rawError: true })
  // async getSharedNotes({ id }: { id: number }) {
  //   console.log('actions:getSharedNotes'); // eslint-disable-line no-console

  //   const result = await API.getSharedNotes(id);

  //   if (result instanceof Error) {
  //     return Promise.reject(result);
  //   }

  //   return Promise.resolve(result);
  // }

  // @Action({ rawError: true })
  // async getPrivateNotes({ id }: { id: number }) {
  //   console.log('actions:getPrivateNotes'); // eslint-disable-line no-console

  //   const result = await API.getPrivateNotes(id);

  //   if (result instanceof Error) {
  //     return Promise.reject(result);
  //   }

  //   return Promise.resolve(result);
  // }

  @Action({ rawError: true })
  async getTargetDateNotes({ id }: { id: number }) {
    console.log('store/operations - actions:getTargetDateNotes'); // eslint-disable-line no-console

    const result = await API.getTargetDateNotes(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  async getSupplyNotes({ id }: { id: number }) {
    console.log('store/operations - actions:getSupplyNotes'); // eslint-disable-line no-console

    const result = await API.getSupplyNotes(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  async getPaymentsNotes({ id }: { id: number }) {
    console.log('store/operations - actions:getPaymentsNotes'); // eslint-disable-line no-console

    const result = await API.getPaymentsNotes(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve(result.notes);
  }

  @Action({ rawError: true })
  async getOperationDates({ id }: { id: number }) {
    console.log('store/operations - actions:getOperationDates'); // eslint-disable-line no-console

    const result = await API.getOperationDates(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  async getContactDetails({ id }: { id: number }) {
    console.log('store/operations - actions:getContactDetails'); // eslint-disable-line no-console

    const result = await API.getContactDetails(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  async getOperations() {
    console.log('store/operations - actions:getOperations'); // eslint-disable-line no-console

    this.context.commit('setOperationsUpdating');

    const params = getParamsFromFilters(this.filters);

    params.offset = this.list.length;
    params.limit = limit;

    const result = await API.getOperations(params);

    if (result instanceof Error) {
      this.context.commit('unsetOperationsUpdating');

      return Promise.reject(result);
    }

    if (result.length === 0) {
      this.context.commit('unsetOperationsUpdating');

      return Promise.resolve(false);
    }

    this.context.commit('addOperationsToList', { operations: result });

    this.context.commit('unsetOperationsUpdating');

    return Promise.resolve(result.length === limit);
  }

  @Action({ rawError: true })
  async getOperationsCount() {
    console.log('store/operations - actions:getOperationsCount'); // eslint-disable-line no-console

    const params = getParamsFromFilters(this.filters);

    const result = await API.getOperationsCount(params);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    const resultCount = Number(result.count);

    this.context.commit('setOperationsCount', resultCount);

    return Promise.resolve();
  }

  @Action({ rawError: true })
  async exportOperations() {
    console.log('store/operations - actions:exportOperations'); // eslint-disable-line no-console

    const params = getParamsFromFilters(this.filters);

    const result = await API.exportOperations(params);

    if (result instanceof Error) {
      // this.context.commit('unsetOperationsExporting');

      return Promise.reject(result);
    }

    // if (result.length === 0) {
    //   this.context.commit('unsetOperationsUpdating');

    //   return Promise.resolve(false);
    // }

    // this.context.commit('addOperationsToList', { operations: result });

    // Avvia il recupero differito di eventuali progetti figli
    // this.context.dispatch('getChildren');

    // this.context.commit('unsetOperationsExporting');

    return Promise.resolve();
  }

  @Action({ rawError: true })
  async exportArchivables() {
    console.log('store/operations - actions:exportArchivables'); // eslint-disable-line no-console

    const result = await API.exportArchivables();

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return Promise.resolve();
  }

  @Action({ rawError: true })
  async exportTimestamps() {
    console.log('store/operations - actions:exportTimestamps'); // eslint-disable-line no-console
    const result = await API.exportTimestamps();
    if (result instanceof Error) {
      return Promise.reject(result);
    }
    return Promise.resolve();
  }

  @Action({ rawError: true })
  async updateOperation({ id }: { id: number }) {
    console.log(`store/operations - actions:updateOperation(${id})`); // eslint-disable-line no-console

    if (!id) Promise.reject(new Error('id non specificato'));

    // Definisce il progetto come in aggiornamento
    Vue.set(this.poolUpdating, id, true);

    // Scarica la versione completa del progetto
    const result = await API.getOperation(id);
    console.log(`store/operations - actions:updateOperation(${id}) result =`, result); // eslint-disable-line no-console

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    // Aggiunge/aggiorna il progetto nel pool
    this.context.commit('addOperationToPool', { operation: result });

    return Promise.resolve(result);
  }

  @Action({ rawError: true })
  async selectOperation({ id }: { id: number }) {
    console.log(`store/operations - actions:selectOperation(${id})`); // eslint-disable-line no-console

    if (!id) Promise.reject(new Error('id non specificato'));

    this.context.commit('setOperationUpdating');

    const result = await API.getOperation(id);

    console.log('store/operations - actions:selectOperation result =', result); // eslint-disable-line no-console

    if (result instanceof Error) {
      this.context.commit('unsetOperationUpdating');
      this.context.dispatch('unselectOperation');

      return Promise.reject(result);
    }

    this.context.commit('setOperation', { operation: result });
    this.context.commit('addOperationToPool', { operation: result, full: true });

    // Aggiorna l'elenco dei progetti figli associati
    // await this.context.dispatch('updateChildrenList');

    // Avvia il recupero differito di eventuali progetti figli
    // this.context.dispatch('getChildren');

    // this.context.commit('unsetDirty');
    this.context.commit('unsetOperationUpdating');

    return Promise.resolve(result);
  }

  @Action({ rawError: true }) // rev 1
  unselectOperation() {
    console.log('store/operations - actions:unselectOperation'); // eslint-disable-line no-console

    this.context.commit('unselectOperation');
    // this.context.commit('unsetDirty');
  }

  @Action({ rawError: true }) // rev 1
  async resolveOperation() {
    console.log('store/operations - actions:resolveOperation'); // eslint-disable-line no-console

    // Se non c'è un progetto selezionato si ferma
    if (!this.operation) return Promise.reject();

    // this.context.commit('setDirty');
    this.context.commit('setOperationUpdating');

    const result = await API.resolveLead(this.operation);

    // this.context.dispatch('refreshLead');

    if (result instanceof Error) {
      this.context.commit('unsetOperationUpdating');

      return Promise.reject(result);
    }

    return result;
  }

  // createOperationZip
  @Action({ rawError: true }) // rev 1
  async createOperationZip({ id }: { id: number }) {
    console.log('store/operations - actions:createOperationZip'); // eslint-disable-line no-console

    const result = await API.createOperationZip(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return result;
  }

  // downloadOperationZip
  @Action({ rawError: true }) // rev 1
  async downloadOperationZip({ id }: { id: number }) {
    console.log('store/operations - actions:downloadOperationZip'); // eslint-disable-line no-console

    const result = await API.downloadOperationZip(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return result;
  }

  // uploadOperationZip
  @Action({ rawError: true }) // rev 1
  async uploadOperationZip({ id }: { id: number }) {
    console.log('store/operations - actions:uploadOperationZip'); // eslint-disable-line no-console

    const result = await API.uploadOperationZip(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return result;
  }

  // verifyOperationZip
  @Action({ rawError: true }) // rev 1
  async verifyOperationZip({ id }: { id: number }) {
    console.log('store/operations - actions:verifyOperationZip'); // eslint-disable-line no-console

    const result = await API.verifyOperationZip(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return result;
  }

  // archiveOperation
  @Action({ rawError: true }) // rev 1
  async archiveOperation({ id }: { id: number }) {
    console.log('store/operations - actions:archiveOperation'); // eslint-disable-line no-console

    const result = await API.archiveOperation(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return result;
  }

  // removeOperationZip
  @Action({ rawError: true }) // rev 1
  async removeOperationZip({ id }: { id: number }) {
    console.log('store/operations - actions:removeOperationZip'); // eslint-disable-line no-console

    const result = await API.removeOperationZip(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return result;
  }

  // checkOperationStatus
  @Action({ rawError: true }) // rev 1
  async checkOperationStatus({ id }: { id: number }) {
    console.log('store/operations - actions:checkOperationStatus'); // eslint-disable-line no-console

    const result = await API.checkOperationStatus(id);

    if (result instanceof Error) {
      return Promise.reject(result);
    }

    return result;
  }

  @Action({ rawError: true })
  async [SOCKET_LEAD_UPDATED](data: SocketLeadUpdatedData) {
    console.log(`store/operations - actions:SOCKET_LEAD_UPDATED(${data.id})`); // eslint-disable-line no-console

    console.log(`store/operations - actions:SOCKET_LEAD_UPDATED(${data.id}) data =`, data); // eslint-disable-line no-console

    // Se è stata creata una nuova lead, non effettua nessun altro controllo.
    // eslint-disable-next-line no-useless-return
    if (data.action === 'created') return;

    // Una lead è stata aggiornata

    // Se la lead corrisponde a una commessa nel pool...
    if (data.id in this.pool) {
      // ... verifico se la sto già aggiornanto
      if (data.id in this.poolUpdating && this.poolUpdating[data.id] === true) {
        // eslint-disable-next-line no-console
        console.log(`store/operations - actions:SOCKET_LEAD_UPDATED(${data.id}) ignorato: commessa in aggiornamento`);
        return;
      }

      if (data.action === 'updated') {
        this.context.dispatch('updateOperation', { id: data.id });
      } else
      if (data.action === 'deleted') {
        // ... la elimino
        this.context.commit('removeOperationFromPool', { id: data.id });
      } else {
        // eslint-disable-next-line no-console
        console.log(`store/operations - actions:SOCKET_LEAD_UPDATED(${data.id}) azione sconosciuta (${data.action})`);
      }
    } else {
      console.log(`store/operations - actions:SOCKET_LEAD_UPDATED(${data.id}) la lead non è nel pool`); // eslint-disable-line no-console

      // Verifico se è tra le figlie
      let fatherId: number | null = null;
      // eslint-disable-next-line no-restricted-syntax
      for (const operationId in this.pool) {
        if (Object.prototype.hasOwnProperty.call(this.pool, operationId)) {
          const operation = this.pool[operationId];
          const childrenIds = getChildrenIds(operation);
          if (childrenIds.includes(data.id)) {
            fatherId = Number(operationId);
            break;
          }
        }
      }

      if (fatherId) {
        console.log(`store/operations - actions:SOCKET_LEAD_UPDATED(${data.id}) trovata la lead padre tra le commesse nel pool con id = ${fatherId}`); // eslint-disable-line no-console
        this.context.dispatch('updateOperation', { id: fatherId });
      } else {
        console.log(`store/operations - actions:SOCKET_LEAD_UPDATED(${data.id}) non è tra le lead del pool e tra le loro figlie`); // eslint-disable-line no-console

        // ... chiede all'utente di aggiornare manualmente l'elenco
        this.context.commit('setOperationsUpdateRequired');
      }
    }
  }

  @Action({ rawError: true })
  [SOCKET_OPERATION_UPDATED](data: LeadUpdatedData) {
    console.log('store/operations - actions:SOCKET_OPERATION_UPDATED data.id =', data.id); // eslint-disable-line no-console
    console.log('store/operations - actions:SOCKET_OPERATION_UPDATED data.action =', data.action); // eslint-disable-line no-console
    console.log('store/operations - actions:SOCKET_OPERATION_UPDATED data.attributes =', data.attributes); // eslint-disable-line no-console

    // La Operation nel pool verrà eventualmente aggiornata soltanto se è stato
    // modificato almeno uno dei seguenti attributi:
    const interestingAttributes = [
      'practiceNotes',

      'practiceAssignee',
      'practiceAssigned',
      'practiceAssignedDate',
      'practiceAssignees',

      'projectAssignee',
      'projectAssigned',
      'projectAssignedDate',
      'projectAssignees',

      'eneaAssignee',
      'eneaAssigned',
      'eneaAssignedDate',
      'eneaAssignees',

      'doganeAssignee',
      'doganeAssigned',
      'doganeAssignedDate',
      'doganeAssignees',

      'praticaDogane',
      'doganeElettricoNotes',
    ];

    switch (data.action) {
      case 'created':
        // Una Operation è stata creata
        // ... chiede all'utente di aggiornare manualmente l'elenco
        this.context.commit('setOperationsUpdateRequired');
        break;

      case 'updated':
        // Una Operation è stata aggiornata
        // Se la lead corrisponde a una Operation nel pool...
        if (data.id in this.pool) {
          // ... verifico se la sto già aggiornando
          if (data.id in this.poolUpdating && this.poolUpdating[data.id] === true) {
            console.log('store/operations - actions:SOCKET_OPERATION_UPDATED ignorato: Operation elencata ma già in aggiornamento'); // eslint-disable-line no-console
          } else
          // eslint-disable-next-line max-len
          if (interestingAttributes.some((attribute) => (data.attributes ?? []).includes(attribute))) {
            console.log('store/operations - actions:SOCKET_OPERATION_UPDATED aggiorno la Operation elencata'); // eslint-disable-line no-console
            this.context.dispatch('updateOperation', { id: data.id });
          } else {
            console.log('store/operations - actions:SOCKET_OPERATION_UPDATED ignorato: gli attributi aggiornati non sono mostrati in elenco'); // eslint-disable-line no-console
          }
        }
        break;

      case 'deleted':
        // Una Operation è stata eliminata
        // ... la elimino
        this.context.commit('removeOperationFromPool', { id: data.id });
        break;

      default:
        console.log('store/operations - actions:SOCKET_OPERATION_UPDATED azione sconosciuta!'); // eslint-disable-line no-console
        break;
    }
  }

  @Action({ rawError: true })
  [SOCKET_ZIP_UPLOAD_PROGRESS](data: ZipProgressData) {
    console.log('store/operations - actions:SOCKET_ZIP_UPLOAD_PROGRESS data =', data); // eslint-disable-line no-console

    this.context.commit('setZipUploadProgress', { progress: data });
  }

  @Action({ rawError: true })
  [SOCKET_ZIP_VERIFY_PROGRESS](data: ZipProgressData) {
    console.log('store/operations - actions:SOCKET_ZIP_VERIFY_PROGRESS data =', data); // eslint-disable-line no-console

    this.context.commit('setZipVerifyProgress', { progress: data });
  }

  @Action({ rawError: true })
  async initOperationsFilterPresets() {
    console.log('store/operations - actions:initOperationsFilterPresets'); // eslint-disable-line no-console

    try {
      const result = await API.getOperationsFilterPresets();
      console.log('store/operations - initOperationsFilterPresets result:', result); // eslint-disable-line no-console
      this.context.commit('setOperationsFilterPresets', result.presets || []);
      console.log('store/operations - preset caricati:', result.presets || []); // eslint-disable-line no-console
    } catch (error) {
      console.error('Errore nel caricamento dei preset Operations:', error); // eslint-disable-line no-console
      this.context.commit('setOperationsFilterPresets', []);
    }
  }

  @Action({ rawError: true })
  async saveOperationsFilterPreset({ name, filters }: { name: string; filters: OperationsFilters }) {
    console.log('store/operations - actions:saveOperationsFilterPreset', { name, filters }); // eslint-disable-line no-console

    try {
      const currentPresets = [...this.operationsFilterPresets];

      // Cerca se esiste già un preset con lo stesso nome
      const existingIndex = currentPresets.findIndex((preset: OperationsFilterPreset) => preset.name === name);

      const newPreset: OperationsFilterPreset = { name, filters };

      if (existingIndex >= 0) {
        // Sovrascrive il preset esistente
        currentPresets[existingIndex] = newPreset;
      } else {
        // Aggiunge un nuovo preset
        currentPresets.push(newPreset);
      }

      await API.updateOperationsFilterPresets({ presets: currentPresets });
      this.context.commit('setOperationsFilterPresets', currentPresets);

      // Ricarica i preset dal server per sincronizzazione
      await this.context.dispatch('initOperationsFilterPresets');
    } catch (error) {
      console.error('Errore nel salvataggio del preset Operations:', error); // eslint-disable-line no-console
      throw error;
    }
  }

  @Action({ rawError: true })
  async loadOperationsFilterPreset(name: string) {
    console.log('store/operations - actions:loadOperationsFilterPreset', { name }); // eslint-disable-line no-console

    const preset = this.operationsFilterPresets.find((p: OperationsFilterPreset) => p.name === name);
    if (!preset) {
      throw new Error(`Preset "${name}" non trovato`);
    }

    // Applica i filtri del preset
    this.context.commit('setOperationsFilters', {
      filters: preset.filters,
      save: false,
      permanent: false,
    });

    return preset.filters;
  }

  @Action({ rawError: true })
  async deleteOperationsFilterPreset(name: string) {
    console.log('store/operations - actions:deleteOperationsFilterPreset', { name }); // eslint-disable-line no-console

    try {
      const currentPresets = [...this.operationsFilterPresets];

      // Filtra il preset da eliminare
      const newPresets = currentPresets.filter((preset: OperationsFilterPreset) => preset.name !== name);

      // Salva l'array aggiornato
      await API.updateOperationsFilterPresets({ presets: newPresets });
      this.context.commit('setOperationsFilterPresets', newPresets);
    } catch (error) {
      console.error('Errore nell\'eliminazione del preset Operations:', error); // eslint-disable-line no-console
      throw error;
    }
  }
}
